/**
 * Shopify API Helpers
 * 
 * Handles all Shopify API interactions:
 * - Customer creation
 * - Pet Metaobject operations
 * - Product fetching
 * - Cart operations
 */

/**
 * Split a single "owner name" into Shopify first/last (first space only). Aligned with Introbox backend.
 * @param {string} raw
 * @returns {{ firstName: string, lastName: string }}
 */
function splitOwnerNameForShopify(raw) {
  if (typeof raw !== 'string') {
    return { firstName: '', lastName: '' };
  }
  const trimmed = raw.replace(/^[\s\uFEFF\u00A0]+|[\s\uFEFF\u00A0]+$/g, '');
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) {
    return { firstName: trimmed, lastName: '' };
  }
  return {
    firstName: trimmed.slice(0, spaceIdx).trim(),
    lastName: trimmed.slice(spaceIdx + 1).replace(/\s+/g, ' ').trim()
  };
}

class ShopifyAPI {
  constructor(flowConfig) {
    this.config = flowConfig;
    this.storefrontApiEndpoint = '/api/2023-10/graphql.json';
    this.customerApiEndpoint = '/account/api/customer';
    this.petMetaobjectType = 'pet'; // Metaobject type handle (to be configured)
    // Backend API endpoint (Cloudflare Worker)
    // This will be set in wizard-1-flow.liquid after backend is deployed
    // For now, use placeholder - will be updated after deployment
    this.backendApiUrl = window.__wizard1BackendApiUrl || null;
    // Request deduplication: track in-flight requests
    this.inFlightRequests = new Map();
  }

  /**
   * Execute Storefront API GraphQL query/mutation
   * @param {string} query - GraphQL query/mutation
   * @param {Object} variables - GraphQL variables
   * @returns {Promise<Object>} GraphQL response
   */
  async executeGraphQL(query, variables = {}, fetchOptions = {}) {
    try {
      const accessToken = this.getCustomerAccessToken();
      const storefrontApiToken = this.config?.storefrontApiToken;
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };

      // Add Storefront API access token (required for all Storefront API requests)
      if (storefrontApiToken) {
        headers['X-Shopify-Storefront-Access-Token'] = storefrontApiToken;
      }

      // Add customer access token if available (for customer-specific queries)
      if (accessToken) {
        headers['X-Shopify-Customer-Access-Token'] = accessToken;
      }

      const response = await fetch(this.storefrontApiEndpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          query: query,
          variables: variables
        }),
        ...fetchOptions
      });

      if (!response.ok) {
        // Create error with status code for better error handling
        const error = new Error(`GraphQL request failed: ${response.statusText}`);
        error.status = response.status;
        error.statusCode = response.status;
        throw error;
      }

      const data = await response.json();
      
      if (data.errors) {
        // Check if this is the expected "missing required arguments" error for anonymous users
        const isAnonymousUserError = data.errors.some(err => 
          err.message && (
            err.message.includes('customerAccessToken') ||
            err.message.includes('missing required arguments') ||
            err.extensions?.code === 'missingRequiredArguments'
          )
        );
        
        if (isAnonymousUserError) {
          // This is expected for anonymous users - return null data instead of throwing
          return null;
        }
        
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      return data.data;
    } catch (error) {
      // Only log as error if it's not a 403 (403 is expected for anonymous users)
      if (error.status !== 403 && error.statusCode !== 403) {
        console.error('Error executing GraphQL:', error);
      }
      throw error;
    }
  }

  /**
   * Get customer access token (if customer is logged in)
   * @returns {string|null} Customer access token or null
   */
  getCustomerAccessToken() {
    // This will be set by Shopify when customer is logged in
    // Check for token in cookies or localStorage
    const token = this.getCookie('customer_access_token');
    return token || null;
  }

  /**
   * Get cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Get language code for Storefront API @inContext(language: X).
   * Uses window.__wizard1CurrentLocale (Wizard pages) or document.documentElement.lang.
   * @returns {string} Uppercase language code (e.g. 'NL', 'EN')
   */
  getStorefrontLanguageCode() {
    const raw = (typeof window !== 'undefined' && window.__wizard1CurrentLocale) ||
      (typeof document !== 'undefined' && document.documentElement?.getAttribute?.('lang')) ||
      'en';
    const primary = String(raw).split('-')[0] || 'en';
    return primary.toUpperCase();
  }

  /**
   * Get country code for Storefront API @inContext(country: X) for multi-currency pricing.
   * Uses window.__wizard1CurrentCountry (from Liquid localization.country.iso_code).
   * @returns {string} Uppercase country code (e.g. 'NL', 'PL')
   */
  getStorefrontCountryCode() {
    const raw = (typeof window !== 'undefined' && window.__wizard1CurrentCountry) || 'NL';
    return String(raw).toUpperCase();
  }

  /**
   * Create customer and Pet Metaobject via backend API
   * @param {Object} customerData - Customer data (ownerName, ownerEmail, emailConsent)
   * @param {Object} petData - Pet data
   * @param {Object} calculations - Calculation results (may be empty on email step)
   * @param {string|null} petMetaobjectId - Existing Pet Metaobject ID (null for new)
   * @param {string|null} loggedInCustomerId - Logged-in customer ID (if customer is logged in)
   * @returns {Promise<Object>} Created customer and Pet Metaobject with IDs
   */
  /**
   * Session / logged-in / state customer id for pet backend calls (wizard-1 or wizard-2).
   * @param {string|null} petMetaobjectId
   * @param {string|null} loggedInCustomerId
   * @returns {string|null}
   */
  _resolveCustomerIdForBackend(petMetaobjectId, loggedInCustomerId = null) {
    const s2 = window.wizard2Flow?.state;
    const s1 = window.wizard1Flow?.state;
    const sessionCustomerId = s2?.sessionCustomerId || s1?.sessionCustomerId;
    const stateCustomerId = s2?.customerId || s1?.customerId;
    return sessionCustomerId
      ? sessionCustomerId
      : loggedInCustomerId
        ? loggedInCustomerId
        : petMetaobjectId && stateCustomerId
          ? stateCustomerId
          : null;
  }

  async createCustomerViaBackend(customerData, petData, calculations = {}, petMetaobjectId = null, loggedInCustomerId = null, extraOptions = {}) {
    // Request deduplication key
    const requestKey = `${customerData.ownerEmail || ''}_${petMetaobjectId || 'new'}`;
    
    // Return existing promise if same request is in-flight
    if (this.inFlightRequests.has(requestKey)) {
      return this.inFlightRequests.get(requestKey);
    }

    // Create new request
    const requestPromise = this._executeBackendRequest(customerData, petData, calculations, petMetaobjectId, loggedInCustomerId, extraOptions);
    
    // Store promise and clean up when done
    this.inFlightRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => this.inFlightRequests.delete(requestKey));
    
    return requestPromise;
  }

  async _executeBackendRequest(customerData, petData, calculations, petMetaobjectId, loggedInCustomerId = null, extraOptions = {}) {
      if (!this.backendApiUrl) {
        console.error('Backend API URL is not configured!');
        throw new Error('Backend API URL is not configured. Please set window.__wizard1BackendApiUrl in wizard-1-flow.liquid after deploying the backend.');
      }

    const emailTrim =
      customerData.ownerEmail != null ? String(customerData.ownerEmail).trim() : '';

    // No email: Introbox customer-pet requires email — persist pet via Shopify metaobject API only.
    if (!emailTrim) {
      const existingCustomerId = this._resolveCustomerIdForBackend(petMetaobjectId, loggedInCustomerId);
      const mo = await this.createOrUpdatePetMetaobject({
        petData,
        calculations,
        customerId: existingCustomerId,
        petMetaobjectId,
        resumeStep: extraOptions.resumeStep ?? null,
        selectedFlavors: extraOptions.selectedFlavors ?? null,
        cartItems: extraOptions.cartItems ?? null
      });
      const splitNoEmail = splitOwnerNameForShopify(customerData.ownerName || '');
      return {
        customer: {
          id: existingCustomerId || null,
          email: '',
          first_name: splitNoEmail.firstName,
          ...(splitNoEmail.lastName ? { last_name: splitNoEmail.lastName } : {})
        },
        customerId: existingCustomerId || null,
        petMetaobjectId: mo.petMetaobjectId
      };
    }

    const existingCustomerId = this._resolveCustomerIdForBackend(petMetaobjectId, loggedInCustomerId);
    
    const requestBody = {
        ownerName: customerData.ownerName || '',
        ownerEmail: emailTrim,
        emailConsent: customerData.emailConsent || false,
        acquisitionFlowId: this.config.acquisitionFlowId || 'wizard-1',
        petData: petData,
        calculations: calculations,
        petMetaobjectId: petMetaobjectId,
        customerId: existingCustomerId,
        resumeStep: extraOptions.resumeStep || null,
        selectedFlavors: extraOptions.selectedFlavors || null,
        cartItems: extraOptions.cartItems || null
      };
      
      let response;
      try {
        response = await fetch(`${this.backendApiUrl}/api/introbox/customer-pet`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
      } catch (fetchError) {
        console.error('Network error:', fetchError.message);
        throw new Error(`Network error: ${fetchError.message}`);
      }

      if (!response.ok) {
        let errorData;
        try {
          const text = await response.text();
          errorData = JSON.parse(text);
        } catch (parseError) {
          errorData = { 
            ok: false, 
            code: 'network_error',
            message: `Request failed: ${response.status} ${response.statusText}` 
          };
        }
        console.error('Backend request failed:', errorData.message || 'Unknown error');
        throw new Error(errorData.message || 'Backend request failed');
      }

      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response JSON:', parseError);
        throw new Error('Invalid JSON response from backend');
      }

      if (!data.ok) {
        console.error('Backend returned error:', data.message || 'Unknown error');
        // Preserve error code for email exists error
        const error = new Error(data.message || 'Backend returned an error');
        error.code = data.code;
        throw error;
      }

      if (!data.customerId || !data.petMetaobjectId) {
        throw new Error('Backend response missing customerId or petMetaobjectId');
      }

      const split = splitOwnerNameForShopify(customerData.ownerName || '');
      return {
        customer: {
          id: data.customerId,
          email: emailTrim,
          first_name: split.firstName,
          ...(split.lastName ? { last_name: split.lastName } : {})
        },
        customerId: data.customerId,
        petMetaobjectId: data.petMetaobjectId
      };
  }

  /**
   * Create or update Pet Metaobject via wizard-1 pet-metaobject endpoint
   * Use for early create (pre-email) and updates with resume fields
   * @param {Object} body - { petData, calculations?, customerId?, petMetaobjectId?, resumeStep?, selectedFlavors?, cartItems? }
   * @returns {Promise<{ petMetaobjectId: string }>}
   */
  async createOrUpdatePetMetaobject(body) {
    if (!this.backendApiUrl) {
      throw new Error('Backend API URL is not configured');
    }

    const response = await fetch(`${this.backendApiUrl}/api/wizard-1/pet-metaobject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        petData: body.petData,
        calculations: body.calculations || {},
        customerId: body.customerId || this._resolveCustomerIdForBackend(body.petMetaobjectId, null),
        ownerEmail: String(body.ownerEmail || body.petData?.ownerEmail || '').trim() || undefined,
        petMetaobjectId: body.petMetaobjectId || null,
        resumeStep: body.resumeStep || null,
        selectedFlavors: body.selectedFlavors || null,
        cartItems: body.cartItems || null
      })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(err.message || 'Pet metaobject request failed');
    }

    const data = await response.json();
    if (!data.ok || !data.petMetaobjectId) {
      throw new Error('Invalid response from pet metaobject endpoint');
    }
    return { petMetaobjectId: data.petMetaobjectId };
  }

  /**
   * @deprecated Use createCustomerViaBackend instead
   * Legacy method kept for backwards compatibility
   */
  async createCustomer(customerData) {
    try {
      const storeDomain = this.getStoreDomain();
      const registerUrl = `${storeDomain}/account/register`;
      

      // Create form data (native form submission format)
      const formData = new URLSearchParams();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('customer[first_name]', customerData.ownerName || '');
      formData.append('customer[email]', customerData.ownerEmail);
      formData.append('customer[accepts_marketing]', customerData.emailConsent ? '1' : '0');

      const response = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include', // Important: include cookies for session
        body: formData.toString()
      });

      // Shopify form submission typically returns:
      // - 302 redirect on success (to /account or /challenge)
      // - 200 with HTML form (if there are errors)
      // - 422 with JSON (if validation fails)

      if (response.status === 302 || response.status === 200) {
        // Success - customer was created
        // Check if we got redirected (success) or got HTML (might have errors)
        const location = response.headers.get('location') || response.url;
        
        // Try to get customer ID from GraphQL query (customer should now be logged in via cookie)
        // Wait a moment for cookies to be set
        await new Promise(resolve => setTimeout(resolve, 500));
        
        try {
          const customerQuery = await this.executeGraphQL(`
            query {
              customer {
                id
                email
                firstName
              }
            }
          `);
          
          if (customerQuery && customerQuery.customer && customerQuery.customer.id) {
            return {
              customer: customerQuery.customer,
              customerId: customerQuery.customer.id
            };
          }
        } catch (gqlError) {
          console.warn('Could not get customer ID from GraphQL (customer may not be logged in yet):', gqlError);
        }
        
        // If GraphQL didn't work, try to extract from response HTML or use email to find customer
        // For now, we'll need to query by email using Admin API or wait for customer to be available
        // Since we can't use Admin API from frontend, we'll throw an error asking to retry
        throw new Error('Customer created but could not retrieve customer ID. Please try again or contact support.');
      }

      // Check for validation errors (422 or JSON response)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorData = await response.json();
        console.error('Customer creation validation errors:', errorData);
        throw new Error(`Customer creation failed: ${JSON.stringify(errorData)}`);
      }

      // If we get HTML, try to parse error messages
      if (contentType.includes('text/html')) {
        const html = await response.text();
        
        // Check for common error indicators in HTML
        if (html.includes('error') || html.includes('already been taken') || html.includes('invalid')) {
          throw new Error('Customer creation failed. Email may already be registered or invalid.');
        }
        
        // If no obvious errors, assume success and try to get customer ID
        throw new Error('Customer creation may have succeeded but could not verify. Please try again.');
      }

      // Unexpected response
      throw new Error(`Unexpected response from customer creation: ${response.status} ${response.statusText}`);
      
    } catch (error) {
      console.error('Error creating customer:', error);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Set customer metafield (custom.acquisition_flow)
   * Only sets if not already set (prevents overwriting)
   * @param {string} customerId - Customer ID (GID format)
   * @param {string} acquisitionFlowId - Acquisition flow ID
   * @returns {Promise<boolean>} True if successful
   */
  async setCustomerAcquisitionFlow(customerId, acquisitionFlowId) {
    try {
      // First check if metafield already exists
      const existingValue = await this.getCustomerAcquisitionFlow(customerId);
      if (existingValue) {
        return true; // Already set, don't overwrite
      }

      const customerGid = customerId.startsWith('gid://') ? customerId : `gid://shopify/Customer/${customerId}`;

      const mutation = `
        mutation customerUpdate($customer: CustomerUpdateInput!) {
          customerUpdate(customer: $customer) {
            customer {
              id
              metafields(first: 10) {
                edges {
                  node {
                    key
                    namespace
                    value
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        customer: {
          id: customerGid,
          metafields: [
            {
              namespace: 'custom',
              key: 'acquisition_flow',
              value: acquisitionFlowId,
              type: 'single_line_text_field'
            }
          ]
        }
      };

      const data = await this.executeGraphQL(mutation, variables);

      if (data.customerUpdate.userErrors && data.customerUpdate.userErrors.length > 0) {
        console.warn(`Customer metafield update errors: ${JSON.stringify(data.customerUpdate.userErrors)}`);
        // Don't throw - this is not critical
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error setting customer acquisition flow:', error);
      // Don't throw - this is not critical, will fall back to cart attributes
      return false;
    }
  }

  /**
   * Get customer metafield (custom.acquisition_flow)
   * @param {string} customerId - Customer ID (GID format)
   * @returns {Promise<string|null>} Acquisition flow ID or null
   */
  async getCustomerAcquisitionFlow(customerId) {
    try {
      const customerGid = customerId.startsWith('gid://') ? customerId : `gid://shopify/Customer/${customerId}`;

      const query = `
        query getCustomer($id: ID!) {
          customer(id: $id) {
            id
            metafield(namespace: "custom", key: "acquisition_flow") {
              value
            }
          }
        }
      `;

      const variables = {
        id: customerGid
      };

      const data = await this.executeGraphQL(query, variables);

      return data.customer?.metafield?.value || null;
    } catch (error) {
      console.error('Error getting customer acquisition flow:', error);
      return null;
    }
  }

  /**
   * Create Pet Metaobject
   * @param {Object} petData - Pet data
   * @param {Object} calculations - Calculation results
   * @param {string} customerId - Customer ID (GID format)
   * @returns {Promise<Object>} Created Pet Metaobject
   */
  async createPetMetaobject(petData, calculations, customerId) {
    try {
      // Convert customer ID to GID format if needed
      const customerGid = customerId.startsWith('gid://') ? customerId : `gid://shopify/Customer/${customerId}`;

      const mutation = `
        mutation petMetaobjectCreate($metaobject: MetaobjectCreateInput!) {
          metaobjectCreate(metaobject: $metaobject) {
            metaobject {
              id
              handle
              fields {
                key
                value
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      // Build metafields object
      const fields = this.buildPetMetaobjectFields(petData, calculations, customerGid);

      const variables = {
        metaobject: {
          type: this.petMetaobjectType,
          fields: fields
        }
      };

      const data = await this.executeGraphQL(mutation, variables);

      if (data.metaobjectCreate.userErrors && data.metaobjectCreate.userErrors.length > 0) {
        throw new Error(`Metaobject creation errors: ${JSON.stringify(data.metaobjectCreate.userErrors)}`);
      }

      return {
        id: data.metaobjectCreate.metaobject.id,
        handle: data.metaobjectCreate.metaobject.handle,
        petName: petData.name
      };
    } catch (error) {
      console.error('Error creating Pet Metaobject:', error);
      throw error;
    }
  }

  /**
   * Build Pet Metaobject fields from pet data and calculations
   * @param {Object} petData - Pet data
   * @param {Object} calculations - Calculation results
   * @param {string} customerGid - Customer GID
   * @returns {Array} Metaobject fields array
   */
  buildPetMetaobjectFields(petData, calculations, customerGid) {
    const fields = [];

    // Input metafields
    if (petData.name) fields.push({ key: 'pet_name', value: petData.name });
    if (petData.gender) fields.push({ key: 'gender', value: petData.gender });
    if (petData.breed) fields.push({ key: 'breed', value: petData.breed });
    if (petData.weight) fields.push({ key: 'weight', value: petData.weight.toString() });
    if (petData.composition) fields.push({ key: 'composition', value: petData.composition });
    if (petData.ageGroup) fields.push({ key: 'age_group', value: petData.ageGroup });
    if (petData.spayed !== null) fields.push({ key: 'spayed', value: petData.spayed.toString() });
    // Activity: If working dog checkbox is checked, override to 'veryActive'
    let activityValue = petData.activity;
    if (petData.isWorkingDog) {
      activityValue = 'veryActive';
    }
    if (activityValue) fields.push({ key: 'activity', value: activityValue });
    if (petData.snacks) fields.push({ key: 'snacks', value: petData.snacks });
    if (petData.allergies && petData.allergies.length > 0) {
      fields.push({ key: 'allergies', value: JSON.stringify(petData.allergies) });
    }
    // Customer reference field: Extract numeric ID from GID format
    // Shopify reference fields expect numeric ID, not full GID
    if (customerGid) {
      // Extract numeric ID from GID format: "gid://shopify/Customer/11588081811720" → "11588081811720"
      // If already numeric, use as-is
      const numericCustomerId = customerGid.startsWith('gid://shopify/Customer/')
        ? customerGid.replace('gid://shopify/Customer/', '')
        : customerGid;
      fields.push({ key: 'customer', value: numericCustomerId });
    }

    // Output metafields (calculations)
    if (calculations.dailyKcal !== null) fields.push({ key: 'dailykcal', value: calculations.dailyKcal.toString() });
    if (calculations.dailyFoodG !== null) fields.push({ key: 'dailyfoodg', value: calculations.dailyFoodG.toString() });
    if (calculations.daysPerBag !== null) fields.push({ key: 'daysperbag', value: calculations.daysPerBag.toString() });
    if (calculations.bagCount !== null) fields.push({ key: 'bagcount', value: calculations.bagCount.toString() });
    if (calculations.matchDays !== null) fields.push({ key: 'matchdays', value: calculations.matchDays.toString() });
    if (calculations.introBagCount !== null) fields.push({ key: 'introbagcount', value: calculations.introBagCount.toString() });
    if (calculations.introMatchDays !== null) fields.push({ key: 'intromatchdays', value: calculations.introMatchDays.toString() });
    
    // Bag size (constant)
    fields.push({ key: 'bagsize', value: '1000' }); // 1000g = 1kg

    return fields;
  }

  /**
   * Update Pet Metaobject
   * @param {string} metaobjectId - Metaobject ID (GID format)
   * @param {Object} petData - Updated pet data
   * @param {Object} calculations - Updated calculations
   * @returns {Promise<Object>} Updated Pet Metaobject
   */
  /**
   * Update Pet Metaobject via backend API
   * This uses the backend API instead of Storefront API to avoid 403 errors
   * @param {string} metaobjectId - Pet Metaobject ID (GID format)
   * @param {Object} petData - Pet data
   * @param {Object} calculations - Calculation results
   * @returns {Promise<Object>} Updated Pet Metaobject with ID
   */
  async updatePetMetaobject(metaobjectId, petData, calculations, loggedInCustomerId = null) {
    try {
      const customerId = loggedInCustomerId || this._resolveCustomerIdForBackend(metaobjectId, null);
      const customerData = {
        ownerEmail: petData.ownerEmail || '',
        ownerName: petData.ownerName || '',
        emailConsent: petData.emailConsent || false
      };

      const result = await this.createCustomerViaBackend(
        customerData,
        petData,
        calculations,
        metaobjectId,
        customerId
      );

      return {
        id: result.petMetaobjectId || metaobjectId,
        handle: null, // Backend doesn't return handle, but that's okay
        petName: petData.name
      };
    } catch (error) {
      console.error('Error updating Pet Metaobject:', error);
      throw error;
    }
  }

  /**
   * Get Pet Metaobject by name
   * @param {string} petName - Pet name
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object|null>} Pet Metaobject or null
   */
  async getPetMetaobjectByName(petName, customerId) {
    try {
      // Get all pets for customer and find by name
      const allPets = await this.getAllPetMetaobjects(customerId);
      return allPets.find(pet => {
        const nameField = pet.fields?.find(f => f.key === 'pet_name');
        return nameField && nameField.value?.toLowerCase() === petName.toLowerCase();
      }) || null;
    } catch (error) {
      console.error('Error getting Pet Metaobject by name:', error);
      return null;
    }
  }

  /**
   * Get all Pet Metaobjects for a customer
   * Note: This uses Storefront API which may return 403 for anonymous users.
   * Returns empty array gracefully if access is denied, allowing fallback to localStorage.
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array<Object>>} Array of Pet Metaobjects
   */
  async getAllPetMetaobjects(customerId) {
    try {
      const customerGid = customerId.startsWith('gid://') ? customerId : `gid://shopify/Customer/${customerId}`;

      const query = `
        query getPetMetaobjects($type: String!, $first: Int!) {
          metaobjects(type: $type, first: $first) {
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;

      const variables = {
        type: this.petMetaobjectType,
        first: 50 // Maximum number of pets per customer
      };

      const data = await this.executeGraphQL(query, variables);

      if (!data.metaobjects || !data.metaobjects.edges) {
        return [];
      }

      // Filter by customer
      const pets = data.metaobjects.edges
        .map(edge => {
          const node = edge.node;
          const customerField = node.fields?.find(f => f.key === 'customer');
          if (customerField && customerField.value === customerGid) {
            return {
              id: node.id,
              handle: node.handle,
              fields: node.fields,
              // Extract common fields for easy access
              petName: node.fields?.find(f => f.key === 'pet_name')?.value,
              customerId: customerField.value
            };
          }
          return null;
        })
        .filter(pet => pet !== null);

      return pets;
    } catch (error) {
      // Gracefully handle 403 errors (anonymous users can't access Metaobjects via Storefront API)
      // This allows the resume functionality to fall back to localStorage
      if (error.status === 403 || error.statusCode === 403 || (error.message && error.message.includes('403'))) {
        console.warn('Cannot access Pet Metaobjects via Storefront API (403). Anonymous users will use localStorage for resume.');
        return [];
      }
      // For other errors, log as error but still return empty array
      console.error('Error getting Pet Metaobjects:', error);
      return [];
    }
  }

  /**
   * Convert numeric ID to Shopify GID format
   * @param {string} id - Numeric ID
   * @param {string} type - Resource type (Product, ProductVariant, etc.)
   * @returns {string} GID format string
   */
  toGid(id, type = 'Product') {
    if (id.startsWith('gid://')) {
      return id;
    }
    return `gid://shopify/${type}/${id}`;
  }

  /**
   * Get product data via Storefront API GraphQL
   * @param {string} productId - Product ID (numeric or GID)
   * @returns {Promise<Object>} Product data with variants
   */
  async getProduct(productId) {
    try {
      const productGid = this.toGid(productId, 'Product');
      
      const query = `
        query getProduct($id: ID!) {
          product(id: $id) {
            id
            title
            handle
            options {
              name
              values
              optionValues {
                name
              }
            }
            variants(first: 250) {
              edges {
                node {
                  id
                  title
                  sku
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        id: productGid
      };

      const data = await this.executeGraphQL(query, variables);

      if (!data.product) {
        throw new Error(`Product ${productId} not found`);
      }

      // Transform GraphQL response to match expected structure
      const product = {
        id: data.product.id,
        title: data.product.title,
        handle: data.product.handle,
        options: (data.product.options || []).map(opt => {
          const fromOptionValues = (opt.optionValues || []).map(ov => ov?.name).filter(Boolean);
          const fromLegacy = opt.values || [];
          return {
            name: opt.name,
            values: fromOptionValues.length ? fromOptionValues : fromLegacy
          };
        }),
        variants: data.product.variants.edges.map(edge => {
          const variant = edge.node;
          return {
            id: variant.id,
            title: variant.title,
            sku: variant.sku,
            selectedOptions: (variant.selectedOptions || []).map(so => ({
              name: so.name,
              value: so.value
            })),
            price: variant.price.amount,
            currency: variant.price.currencyCode,
            available: variant.availableForSale,
            // ProductVariant does not support 'featuredImage' in Storefront API.
            image: variant.image?.url || null,
            imageAlt: variant.image?.altText || variant.title,
            imageData: variant.image
          };
        })
      };

      return product;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  /**
   * Get variant data via Storefront API GraphQL
   * @param {string} variantId - Variant ID (numeric or GID)
   * @returns {Promise<Object>} Variant data with image
   */
  async getVariant(variantId) {
    try {
      const variantGid = this.toGid(variantId, 'ProductVariant');
      const languageCode = this.getStorefrontLanguageCode();
      const countryCode = this.getStorefrontCountryCode();

      const query = `
        query getVariant($id: ID!, $language: LanguageCode, $country: CountryCode) @inContext(language: $language, country: $country) {
          node(id: $id) {
            ... on ProductVariant {
              id
              title
              sku
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              availableForSale
              image {
                url
                altText
                width
                height
              }
              metafield(namespace: "custom", key: "tagline") {
                value
              }
              variantDescription: metafield(namespace: "custom", key: "variant_description") {
                value
              }
              ingredients: metafield(namespace: "custom", key: "ingredients") {
                value
              }
              nutritionDry: metafield(namespace: "custom", key: "nutrition_dry") {
                value
              }
              nutritionAsfed: metafield(namespace: "custom", key: "nutrition_asfed") {
                value
              }
              flavor: metafield(namespace: "custom", key: "Flavor") {
                value
              }
              product {
                id
                title
                handle
              }
            }
          }
        }
      `;

      const variables = {
        id: variantGid,
        language: languageCode,
        country: countryCode
      };

      const data = await this.executeGraphQL(query, variables);

      if (!data.node) {
        throw new Error(`Variant ${variantId} not found`);
      }

      const variant = data.node;

      // Transform GraphQL response to match expected structure
      return {
        id: variant.id,
        title: variant.title,
        sku: variant.sku,
        price: variant.price.amount,
        compareAtPrice: variant.compareAtPrice?.amount || null,
        currency: variant.price.currencyCode,
        available: variant.availableForSale,
        // ProductVariant only has 'image' field, not 'featuredImage'
        image: variant.image?.url || null,
        imageAlt: variant.image?.altText || variant.title,
        imageData: variant.image,
        tagline: variant.metafield?.value || null,
        product: variant.product,
        // Modal metafields - using aliases from GraphQL query
        variantDescription: variant.variantDescription?.value || null,
        ingredients: variant.ingredients?.value || null,
        nutritionDry: variant.nutritionDry?.value || null,
        nutritionAsfed: variant.nutritionAsfed?.value || null,
        flavor: variant.flavor?.value || variant.title || null
      };
    } catch (error) {
      console.error('Error fetching variant:', error);
      throw error;
    }
  }

  /**
   * Get product metafields for modal content
   * @param {string} productId - Product ID (numeric or GID)
   * @returns {Promise<Object>} Product metafields (feeding_advice, storage)
   */
  async getProductMetafields(productId) {
    try {
      const productGid = this.toGid(productId, 'Product');
      
      const query = `
        query getProductMetafields($id: ID!) {
          node(id: $id) {
            ... on Product {
              id
              feedingAdvice: metafield(namespace: "custom", key: "feeding_advice") {
                value
              }
              storage: metafield(namespace: "custom", key: "storage") {
                value
              }
            }
          }
        }
      `;

      const variables = {
        id: productGid
      };

      const data = await this.executeGraphQL(query, variables);

      if (!data.node) {
        throw new Error(`Product ${productId} not found`);
      }

      const product = data.node;

      return {
        productId: product.id,
        feedingAdvice: product.feedingAdvice?.value || null,
        storage: product.storage?.value || null
      };
    } catch (error) {
      console.error('Error fetching product metafields:', error);
      throw error;
    }
  }

  /**
   * Add items to cart
   * @param {Array<Object>} items - Cart items with variant ID, quantity, selling plan ID
   * @returns {Promise<Object>} Cart object
   */
  async addToCart(items) {
    try {
      const cartItems = items.map(item => ({
        id: item.variantId,
        quantity: item.quantity,
        properties: item.properties || {},
        selling_plan: item.sellingPlanId ? item.sellingPlanId : undefined
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: cartItems
        })
      });

      if (!response.ok) {
        const text = await response.text();
        let detail = response.statusText || `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(text);
          if (errJson.description) detail = errJson.description;
          else if (errJson.message) detail = errJson.message;
        } catch (parseErr) {
          if (text && text.length < 500) detail = text;
        }
        const err = new Error(detail);
        err.status = response.status;
        throw err;
      }

      const cart = await response.json();
      if (typeof window.bzStampVwoExperiments === 'function') {
        try {
          await window.bzStampVwoExperiments();
        } catch (e) {}
      }
      return cart;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Change an existing cart line (quantity, selling plan, properties) via /cart/change.js.
   * @param {{ key?: string, id?: string, quantity?: number, sellingPlanId?: string, clearSellingPlan?: boolean, properties?: Object }} opts
   * @returns {Promise<Object>} Cart object
   */
  async changeCartLine(opts) {
    const key = String(opts?.key || opts?.id || '').trim();
    if (!key) throw new Error('changeCartLine requires a line key');
    const body = {
      id: key,
      quantity: opts?.quantity != null ? opts.quantity : 1
    };
    if (opts?.clearSellingPlan === true) {
      body.selling_plan = null;
    } else if (opts?.sellingPlanId) {
      body.selling_plan = opts.sellingPlanId;
    }
    if (opts?.properties && typeof opts.properties === 'object') {
      body.properties = opts.properties;
    }
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const text = await response.text();
      let detail = response.statusText || `HTTP ${response.status}`;
      try {
        const errJson = JSON.parse(text);
        if (errJson.description) detail = errJson.description;
        else if (errJson.message) detail = errJson.message;
      } catch (parseErr) {
        if (text && text.length < 500) detail = text;
      }
      const err = new Error(detail);
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  /**
   * Update cart attributes
   * @param {Object} attributes - Cart attributes
   * @returns {Promise<Object>} Updated cart
   */
  async updateCartAttributes(attributes) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: attributes
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update cart attributes: ${response.statusText}`);
      }

      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      throw error;
    }
  }

  /**
   * Update cart note
   * @param {string} note - Cart note text
   * @returns {Promise<Object>} Updated cart
   */
  async updateCartNote(note) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          note: note
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update cart note: ${response.statusText}`);
      }

      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error updating cart note:', error);
      throw error;
    }
  }

  /**
   * Resolve a MediaImage CDN URL by GID (when metaobject reference is missing).
   * @param {string} mediaId
   * @returns {Promise<string>}
   */
  async getMediaImageUrl(mediaId) {
    if (!mediaId || String(mediaId).indexOf('gid://') !== 0) return '';

    const query = `
      query getMediaImage($id: ID!) {
        node(id: $id) {
          ... on MediaImage {
            image {
              url
            }
          }
        }
      }
    `;

    const data = await this.executeGraphQL(query, { id: mediaId }, { cache: 'no-store' });
    return data?.node?.image?.url || '';
  }

  /**
   * Get Pet Metaobject by ID
   * Note: This uses Storefront API which may return 403 for anonymous users.
   * Returns null gracefully if access is denied, allowing fallback to localStorage.
   * @param {string} metaobjectId - Metaobject ID (GID format)
   * @returns {Promise<Object|null>} Pet Metaobject or null
   */
  async getPetMetaobjectById(metaobjectId, options = {}) {
    try {
      const query = `
        query getMetaobject($id: ID!) {
          metaobject(id: $id) {
            id
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
                ... on GenericFile {
                  url
                }
              }
            }
          }
        }
      `;

      const variables = {
        id: metaobjectId
      };

      const fetchOptions = options.bustCache ? { cache: 'no-store' } : {};
      const data = await this.executeGraphQL(query, variables, fetchOptions);

      if (!data.metaobject) {
        return null;
      }

      const node = data.metaobject;
      const getField = (key) => node.fields?.find(f => f.key === key)?.value;
      let resumeStep = getField('resume_step');
      let selectedFlavors = getField('selected_flavors');
      let cartItems = getField('cart_items');
      if (selectedFlavors && typeof selectedFlavors === 'string') {
        try {
          selectedFlavors = JSON.parse(selectedFlavors);
        } catch (e) {
          selectedFlavors = null;
        }
      }
      if (cartItems && typeof cartItems === 'string') {
        try {
          cartItems = JSON.parse(cartItems);
        } catch (e) {
          cartItems = null;
        }
      }
      const result = {
        id: node.id,
        handle: node.handle,
        fields: node.fields,
        petName: getField('pet_name'),
        customerId: getField('customer'),
        resumeStep: resumeStep || null,
        selectedFlavors: Array.isArray(selectedFlavors) ? selectedFlavors : null,
        cartItems: Array.isArray(cartItems) ? cartItems : null
      };

      return result;
    } catch (error) {
      // Gracefully handle 403 errors (anonymous users can't access via Storefront API)
      // This allows the resume functionality to fall back to localStorage
      if (error.message && error.message.includes('403')) {
        console.warn('Cannot access Pet Metaobject via Storefront API (403). Falling back to localStorage.');
        return null;
      }
      console.error('Error getting Pet Metaobject by ID:', error);
      return null;
    }
  }

  /**
   * Delete Pet Metaobject
   * @param {string} metaobjectId - Metaobject ID (GID format)
   * @returns {Promise<boolean>} True if successful
   */
  async deletePetMetaobject(metaobjectId) {
    try {
      const mutation = `
        mutation metaobjectDelete($id: ID!) {
          metaobjectDelete(id: $id) {
            deletedId
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        id: metaobjectId
      };

      const data = await this.executeGraphQL(mutation, variables);

      if (data.metaobjectDelete.userErrors && data.metaobjectDelete.userErrors.length > 0) {
        throw new Error(`Metaobject deletion errors: ${JSON.stringify(data.metaobjectDelete.userErrors)}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting Pet Metaobject:', error);
      throw error;
    }
  }

  /**
   * Get breed list from Shopify Metafield
   * @returns {Promise<Array<string>>} Array of breed names
   */
  async getBreedList() {
    try {
      // This will fetch from Shopify Metafield definition
      // For now, return empty array
      // TODO: Implement fetching from Metafield definition
      // Note: This requires the Metafield definition ID for the breed field
      
      return [];
    } catch (error) {
      console.error('Error fetching breed list:', error);
      return [];
    }
  }

}

/**
 * Canonical pet name on subscription line items: `_pet_name` (hidden in storefront).
 * Legacy fallbacks: 🐶, `pet_name`. Used for cart note, Klaviyo grouping, etc.
 * @param {Object|null|undefined} properties - Shopify cart/order line item.properties
 * @returns {string} trimmed pet name or ''
 */
function getPetNameFromLineItemProperties(properties) {
  if (!properties || typeof properties !== 'object') return '';
  const raw = properties._pet_name ?? properties['🐶'] ?? properties.pet_name;
  if (raw == null) return '';
  const s = String(raw).trim();
  return s;
}

if (typeof window !== 'undefined') {
  window.getPetNameFromLineItemProperties = getPetNameFromLineItemProperties;
}

// Export for global access
window.ShopifyAPI = ShopifyAPI;

